import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Send, Trash2, UserPlus, Eye, X } from "lucide-react";
import { api, API_BASE } from "@/lib/api";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function MagNewsletter() {
  const [cfg, setCfg] = useState(null);
  const [subs, setSubs] = useState([]);
  const [active, setActive] = useState(0);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(false);

  const load = () => {
    api.get("/mag/digest").then((r) => setCfg(r.data));
    api.get("/mag/subscribers").then((r) => { setSubs(r.data.subscribers || []); setActive(r.data.active || 0); });
  };
  useEffect(load, []);
  if (!cfg) return <div className="p-8 text-sm text-ink-muted">Loading…</div>;

  const saveCfg = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    try {
      await api.put("/mag/digest", { digest_enabled: next.digest_enabled, digest_day: next.digest_day, digest_hour: next.digest_hour });
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const sendNow = async () => {
    setBusy("send");
    try {
      const r = await api.post("/mag/digest/send-now");
      toast.success(`Digest sent to ${r.data.sent} subscriber${r.data.sent === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    setBusy("");
  };
  const addSub = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy("add");
    try {
      await api.post("/mag/subscribers", { email });
      setEmail(""); toast.success("Subscriber added"); load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Failed to add"); }
    setBusy("");
  };
  const delSub = async (id) => {
    try {
      await api.delete(`/mag/subscribers/${id}`);
      setSubs((s) => s.filter((x) => x.subscriber_id !== id));
    } catch { toast.error("Failed to remove"); }
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="mag-newsletter-page">
      <div>
        <h1 className="font-display text-2xl font-bold">Newsletter Digest</h1>
        <p className="text-sm text-ink-secondary">A weekly email with your featured story + the 5 newest stories from the past 7 days.</p>
      </div>

      {!cfg.email_configured && (
        <div className="rounded-xl border border-amber-500/50 bg-amber-500/10 p-3 text-xs text-amber-300" data-testid="mag-digest-config-warning">
          Email sending isn't configured on this server (RESEND_API_KEY missing) — digests can't be delivered yet.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-4">
          <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-3" data-testid="mag-digest-settings">
            <div className="flex items-center gap-2 text-sm font-bold"><Mail className="h-4 w-4 text-brand-amber" /> Weekly Digest</div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!cfg.digest_enabled} onChange={(e) => saveCfg({ digest_enabled: e.target.checked })} data-testid="mag-digest-enabled-toggle" />
              Send the digest automatically every week
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Day of Week</div>
                <select value={cfg.digest_day} onChange={(e) => saveCfg({ digest_day: Number(e.target.value) })} data-testid="mag-digest-day-select"
                  className="w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-sm">
                  {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Time (UTC)</div>
                <select value={cfg.digest_hour} onChange={(e) => saveCfg({ digest_hour: Number(e.target.value) })} data-testid="mag-digest-hour-select"
                  className="w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-sm">
                  {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
                </select>
              </div>
            </div>
            <div className="text-xs text-ink-muted" data-testid="mag-digest-preview-count">
              {cfg.preview_count > 0
                ? `${cfg.preview_count} stor${cfg.preview_count === 1 ? "y" : "ies"} from the past 7 days ready for the next digest.`
                : "No stories published in the past 7 days yet — the digest skips empty weeks."}
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setPreview(true)} data-testid="mag-digest-preview-btn"
                className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-1.5 text-xs font-bold">
                <Eye className="h-3.5 w-3.5" /> Preview digest
              </button>
              <button onClick={sendNow} disabled={!!busy || !cfg.subscribers} data-testid="mag-digest-send-now-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-amber text-black px-4 py-1.5 text-xs font-bold disabled:opacity-50">
                {busy === "send" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Send digest now
              </button>
            </div>
            {!cfg.subscribers && <div className="text-[11px] text-ink-muted">Add subscribers first — readers can also join via the subscribe form on your public magazine.</div>}
          </div>

          <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-2" data-testid="mag-digest-history">
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Recent Sends</div>
            {!cfg.sends?.length && <div className="text-xs text-ink-muted">No digests sent yet.</div>}
            {(cfg.sends || []).map((s) => (
              <div key={s.send_id} className="border-b border-line/50 pb-2 last:border-0">
                <div className="text-xs font-semibold truncate">{s.subject}</div>
                <div className="text-[11px] text-ink-muted">{(s.created_at || "").slice(0, 16).replace("T", " ")} UTC · {s.sent}/{s.total} delivered · {s.source}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-3" data-testid="mag-subscribers-card">
          <div className="flex items-center justify-between">
            <div className="text-sm font-bold">Subscribers</div>
            <span className="rounded-full border border-line px-2.5 py-0.5 text-[11px] font-bold" data-testid="mag-subscriber-count">{active} active</span>
          </div>
          <form onSubmit={addSub} className="flex gap-2">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="reader@email.com" data-testid="mag-add-subscriber-input"
              className="flex-1 rounded-md border border-line bg-bg-elevated px-2.5 py-1.5 text-sm" />
            <button type="submit" disabled={busy === "add"} data-testid="mag-add-subscriber-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1.5 text-xs font-bold">
              {busy === "add" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />} Add
            </button>
          </form>
          <div className="max-h-96 overflow-y-auto space-y-1">
            {!subs.length && <div className="text-xs text-ink-muted">No subscribers yet. A subscribe form is live on your public magazine pages.</div>}
            {subs.map((s) => (
              <div key={s.subscriber_id} className="flex items-center justify-between gap-2 rounded-md border border-line/50 px-2.5 py-1.5" data-testid={`mag-subscriber-row-${s.subscriber_id}`}>
                <div className="min-w-0">
                  <div className="text-xs truncate">{s.email}</div>
                  <div className="text-[10px] text-ink-muted">{s.status === "subscribed" ? (s.source === "manual" ? "added manually" : "via subscribe form") : "unsubscribed"} · {(s.created_at || "").slice(0, 10)}</div>
                </div>
                <button onClick={() => delSub(s.subscriber_id)} data-testid={`mag-delete-subscriber-${s.subscriber_id}`}
                  className="text-ink-muted hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="mag-digest-preview-modal">
          <div className="bg-bg-surface border border-line rounded-xl w-full max-w-2xl h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-line">
              <div className="font-display font-bold text-sm">Digest Preview — exactly what subscribers receive</div>
              <button onClick={() => setPreview(false)} data-testid="mag-digest-preview-close" className="text-ink-muted hover:text-ink-primary"><X className="h-5 w-5" /></button>
            </div>
            <iframe title="Digest preview" src={`${API_BASE}/mag/digest/preview`} className="flex-1 w-full bg-white" data-testid="mag-digest-preview-iframe" />
          </div>
        </div>
      )}
    </div>
  );
}
